import type { Event } from '@sse/schema';

/** Errore dell'API con il corpo JSON (es. { error, issues } o { error: 'version_conflict', event }). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: {
      error?: string;
      issues?: Array<{ path: string; message: string }>;
      event?: Event;
      /** evento unito a un altro (GET /events/:id) */
      mergedInto?: string;
    },
  ) {
    super(body.error ?? `HTTP ${status}`);
  }
}

export type Api = <T>(path: string, init?: { method?: string; body?: unknown }) => Promise<T>;

/** Client dell'API admin: token Cognito nell'header, errori come ApiError. */
export function createApi(
  baseUrl: string,
  token: () => Promise<string | undefined>,
  onUnauthorized: () => void,
): Api {
  return async <T>(path: string, init: { method?: string; body?: unknown } = {}) => {
    const res = await fetch(`${baseUrl}/v1/admin${path}`, {
      method: init.method ?? 'GET',
      headers: {
        Authorization: `Bearer ${(await token()) ?? ''}`,
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });
    if (res.status === 401) onUnauthorized();
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) throw new ApiError(res.status, body);
    return body as T;
  };
}

/** Riga dell'elenco (GET /admin/events). */
export interface AdminEventRow {
  id: string;
  slug: string;
  title: string;
  status: Event['status'];
  category: Event['category'];
  comuneId: string;
  comune: string;
  frazione?: string;
  startDate: string;
  endDate: string;
  updatedAt: string;
  featured: boolean;
  locked: number;
  hasImage: boolean;
}

export interface AuditEntry {
  at: string;
  by: { sub: string; email?: string };
  action: 'create' | 'update' | 'unlock';
  changes: Record<string, { from: unknown; to: unknown }>;
  /** unione con un altro evento (che è stato eliminato) */
  merge?: { from: string; title: string; slug: string };
}

// --- doppioni e unione di due eventi (docs/13-api.md §3) -----------------------------------

export interface DuplicateSide {
  id: string;
  slug: string;
  title: string;
  status: Event['status'];
  category: Event['category'];
  comune: string;
  frazione?: string;
  startDate: string;
  endDate: string;
  hasImage: boolean;
  version: number;
  updatedAt: string;
}

export interface DuplicatePair {
  score: number;
  titleScore: number;
  a: DuplicateSide;
  b: DuplicateSide;
}

export const MERGE_FIELDS = ['title', 'dates', 'place', 'category', 'texts', 'links'] as const;
export type MergeField = (typeof MERGE_FIELDS)[number];

/** Anteprima dell'unione (POST /events/merge/preview): `event` = evento che resta (target). */
export interface EventMergePreview {
  event: Event;
  changes: Record<string, unknown>;
  origin: Record<MergeField, 'event' | 'candidate' | 'filled'>;
  available: Record<MergeField, { event: boolean; candidate: boolean }>;
  target: Event;
  source: Event;
}

export interface EventDetail {
  event: Event;
  audit: AuditEntry[];
  sources: Array<{ ref: string; sourceId?: string; url?: string }>;
}

// --- import automatici (GET/POST /admin/imports/*) ----------------------------------------

export type Outcome = 'new' | 'duplicate' | 'update' | 'uncertain' | 'invalid';
export type CandidateStatus = 'pending' | 'needs_review' | 'applied' | 'rejected';

export interface RunCounters {
  items: number;
  fetched: number;
  unchanged: number;
  changed: number;
  failed: number;
  extracted: number;
  candidates: number;
  resolved: number;
  new: number;
  duplicate: number;
  update: number;
  uncertain: number;
  invalid: number;
  published: number;
}

export interface ImportRun {
  id: string;
  sourceId: string;
  trigger: 'schedule' | 'manual';
  by?: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt?: string;
  counters: Partial<RunCounters>;
  quality: Record<string, Record<string, unknown>>;
  error?: string;
}

export interface ImportSource {
  id: string;
  name: string;
  adapter: string;
  url?: string;
  schedule?: string;
  trust: string;
  enabled: boolean;
  autoPublish: boolean;
  paused: boolean;
  pausedAt?: string;
  lastRun: ImportRun | null;
  /** decisioni che hanno seguito l'azione suggerita (incerti esclusi) */
  accuracy?: {
    total: number;
    agreed: number;
    uncertain: number;
    byOutcome: Partial<Record<string, { total: number; agreed: number }>>;
  };
}

/** Problema segnalato dal sito su un evento pubblicato. */
export interface EventReport {
  id: string;
  status: 'open' | 'resolved' | 'dismissed';
  topic: string;
  /** promozione richiesta da chi organizza: portal, social */
  promo?: string[];
  note: string;
  event: { id: string; slug: string; title: string };
  contact?: { email?: string; name?: string; phone?: string; kind?: string; organization?: string };
  createdAt: string;
  closedAt?: string;
  closedBy?: { sub: string; email?: string };
  reply?: string;
  /** cosa ha fatto la redazione sull'evento (segnalazioni chiuse come corrette) */
  outcome?: ReportOutcome;
  /** stato attuale dell'evento; `deleted` se non c'è più */
  eventStatus?: Event['status'] | 'deleted';
}

export type ReportOutcome = 'corrected' | 'cancelled' | 'removed' | 'promoted';

export interface Review {
  action: 'create' | 'merge' | 'reject';
  by: { sub: string; email?: string };
  at: string;
  eventId?: string;
  /** scarto: motivo (REJECT_REASONS) e nota */
  reason?: string;
  note?: string;
}

export interface CandidateRow {
  id: string;
  sourceId: string;
  runId?: string;
  status: CandidateStatus;
  title: string;
  comuneId?: string;
  comune: string;
  frazione?: string;
  startDate: string;
  endDate: string;
  category: Event['category'];
  outcome?: Outcome;
  confidence: number;
  issues: number;
  match?: { kind: 'event' | 'candidate'; id: string; title: string; score: number };
  enriched: boolean;
  /** pagina pubblica della sorgente (siti web) */
  sourceUrl?: string;
  review?: Review;
  createdAt: string;
  version: number;
}

export interface DedupMatch {
  kind: 'event' | 'candidate';
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  score: number;
  titleScore: number;
  dateScore: number;
  status?: string;
}

/** Candidato completo (GET /admin/imports/candidates/:id). */
export interface CandidateDetail {
  candidate: {
    id: string;
    sourceId: string;
    externalKey: string;
    runId?: string;
    extracted: {
      title: string;
      placeText: string;
      description?: string;
      priceText?: string;
      dates: Event['dates'];
      category?: Event['category'];
    };
    normalized?: {
      title: string;
      category: Event['category'];
      comuneId?: string;
      frazione?: string;
      venue?: Event['venue'];
      dates: Event['dates'];
      startDate: string;
      endDate: string;
      links?: Event['links'];
    };
    issues: Array<{ code: string; severity: string; message: string }>;
    confidence: number;
    decision?: {
      outcome: Outcome;
      matchEventId?: string;
      matchCandidateId?: string;
      scores: Record<string, number>;
      by: string;
      at: string;
    };
    matches: DedupMatch[];
    enriched?: {
      description: string;
      shortDesc: string;
      seoTitle: string;
      seoDescription: string;
      category?: Event['category'];
      model: string;
      promptVersion: string;
    };
    review?: Review;
    status: CandidateStatus;
    version: number;
  };
  row: CandidateRow;
  events: Event[];
  /** locandina presa dalla sorgente (link firmato, 10 minuti): diventa l'immagine dell'evento */
  poster?: { url: string };
  /** segnalazione dal sito: chi l'ha mandata e la locandina originale (link firmato, 10 minuti) */
  submission?: {
    id: string;
    createdAt: string;
    email?: string;
    name?: string;
    phone?: string;
    /** chi è (profilo: privato, associazione, Comune…) e nome dell'ente o dell'attività */
    kind?: string;
    organization?: string;
    link?: string;
    posterUrl?: string;
    posterType?: string;
    /** profilo di chi ha segnalato (assente se cancellato o inserito dal bot) */
    userSub?: string;
    /** segnalatore fidato: le sue segnalazioni si pubblicano da sole se il caso è sicuro */
    trusted?: boolean;
    /** inserito dalla redazione con il bot Telegram */
    fromTelegram?: boolean;
  };
}
